/* eslint-disable camelcase -- snake_case fields come straight from the mandate API shape */
import { Mandate } from "creso-core/types";
import { MandateStatus, MandateType } from "creso-core/constants";
import { Button, cn } from "web-design-system";
import { Download, Loader2, Trash2Icon } from "lucide-react";
import { convertToCurrency } from "creso-core/utils";
import { format, parse } from "date-fns";
import { useCallback, useState } from "react";
import { getCORSSafeURL } from "../../utils/url";

interface Props {
  mandate: Mandate;
  className?: string;
  investmentAccountId?: string;
  /** Advisor action: emails the investor an authorisation link. */
  onAuthorize?: () => void;
  /** Investor action: sends the current user to their bank to authorise. */
  onSelfAuthorize?: () => void;
  onSelect?: (mandate: Mandate) => void;
  onDownload?: (mandate: Mandate) => Promise<void>;
  onDelete?: (mandate: Mandate) => Promise<void>;
  isNotifying?: boolean;
}

function showActiveStatusBox(mandate: Mandate) {
  return mandate.status === MandateStatus.APPROVED && !!mandate.remark;
}

function showActiveHeaderBadge(mandate: Mandate) {
  return (
    mandate.status === MandateStatus.ACTIVE && !showActiveStatusBox(mandate)
  );
}

function canPreviewMandate(mandate: Mandate) {
  return mandate.mandate_type === MandateType.X && !!mandate.mandate_image_file;
}

function shouldShowStatusCard(mandate: Mandate) {
  const { status, mandate_type, mandate_image_file } = mandate;

  if (showActiveStatusBox(mandate)) return true;

  if (status === MandateStatus.PENDING) {
    if (mandate_type === MandateType.E) return true;
    if (mandate_type === MandateType.X && !mandate_image_file) return true;
    return false;
  }

  if (status === MandateStatus.UNDER_PROCESSING) return true;

  if (status === MandateStatus.REJECTED) return true;

  return false;
}

function showUploadSignedForm(mandate: Mandate) {
  return (
    mandate.status === MandateStatus.PENDING &&
    mandate.mandate_type === MandateType.X &&
    !mandate.mandate_image_file
  );
}

function getStatusColor(status: string) {
  switch (status) {
    case MandateStatus.REJECTED:
      return "text-red-700";
    case MandateStatus.CANCELLED:
      return "text-orange-700";
    case MandateStatus.UNDER_PROCESSING:
      return "text-violet-700";
    case MandateStatus.ACTIVE:
      return "text-green-700";
    case MandateStatus.APPROVED:
      return "text-green-700";
    case MandateStatus.PENDING:
      return "text-yellow-600";
    default:
      return "text-foreground";
  }
}

function formatMandateEndDate(endDate: string) {
  return format(parse(endDate, "dd/MM/yyyy", new Date()), "dd MMM yyyy");
}

type StatusCardVariant = "neutral" | "success" | "error";

type MandateStatusCardProps = {
  mandate: Mandate;
  onNotify: () => void;
  onSelfAuthorize?: () => void;
  onDownload: () => void;
  onPreview: () => void;
  isNotifying: boolean;
  isDownloading: boolean;
  showNotifyAction: boolean;
  showDownloadAction: boolean;
  showReviewAction: boolean;
};

function MandateStatusCard({
  mandate,
  onNotify,
  onSelfAuthorize,
  onDownload,
  onPreview,
  isNotifying,
  isDownloading,
  showNotifyAction,
  showDownloadAction,
  showReviewAction,
}: MandateStatusCardProps) {
  const { status, mandate_type, nse_rejection_reason } = mandate;
  const showPreview = canPreviewMandate(mandate);

  let variant: StatusCardVariant = "neutral";
  let title = "";
  let subtext: string | undefined;
  let primaryAction: React.ReactNode = null;
  let secondaryAction: React.ReactNode = null;

  if (showActiveStatusBox(mandate)) {
    variant = "success";
    title = "Approved";
    subtext = mandate.remark ?? undefined;
  } else if (
    status === MandateStatus.PENDING &&
    mandate_type === MandateType.E
  ) {
    title = "Pending approval";
    if (onSelfAuthorize) {
      primaryAction = (
        <Button
          size="sm"
          variant="outline"
          onClick={onSelfAuthorize}
          disabled={isNotifying}
        >
          Authorise
        </Button>
      );
    } else if (showReviewAction) {
      primaryAction = (
        <Button
          variant="outline"
          size="sm"
          onClick={onNotify}
          disabled={isNotifying}
        >
          Notify client
        </Button>
      );
    }
  } else if (
    status === MandateStatus.PENDING &&
    mandate_type === MandateType.X &&
    !mandate.mandate_image_file
  ) {
    title = "Client signature required";
    subtext = "Download the mandate form or notify client";
    if (showNotifyAction) {
      primaryAction = (
        <Button
          variant="outline"
          size="sm"
          onClick={onNotify}
          disabled={isNotifying}
        >
          Notify
        </Button>
      );
    }
    if (showDownloadAction) {
      secondaryAction = (
        <Button
          variant="outline"
          size="icon"
          className="size-8 shrink-0"
          onClick={onDownload}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
        </Button>
      );
    }
  } else if (status === MandateStatus.UNDER_PROCESSING) {
    title = "Under processing";
    subtext = mandate.remark ?? undefined;
    if (showPreview) {
      primaryAction = (
        <Button variant="outline" size="sm" onClick={onPreview}>
          Preview
        </Button>
      );
    }
  } else if (status === MandateStatus.REJECTED) {
    variant = "error";
    title = "Rejected";
    subtext = nse_rejection_reason ?? undefined;
    if (showPreview) {
      primaryAction = (
        <Button variant="outline" size="sm" onClick={onPreview}>
          Preview
        </Button>
      );
    }
  }

  const variantStyles: Record<
    StatusCardVariant,
    { title: string; subtext: string }
  > = {
    neutral: {
      title: "text-muted-foreground",
      subtext: "text-muted-foreground",
    },
    success: {
      title: "text-green-700 font-semibold",
      subtext: "text-muted-foreground",
    },
    error: {
      title: "text-red-700 font-semibold",
      subtext: "text-red-700",
    },
  };

  const styles = variantStyles[variant];
  const hasActions = primaryAction || secondaryAction;

  return (
    <div className="rounded-lg bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className={cn("text-sm font-medium", styles.title)}>
            {title}
          </span>
          {subtext ? (
            <span className={cn("text-sm", styles.subtext)}>{subtext}</span>
          ) : null}
        </div>
        {hasActions ? (
          <div className="flex shrink-0 items-center gap-2">
            {primaryAction}
            {secondaryAction}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function MandateItem(props: Props) {
  const {
    mandate,
    onSelect,
    onAuthorize,
    onSelfAuthorize,
    onDownload,
    onDelete,
    isNotifying = false,
  } = props;

  const [isDownloading, setIsDownloading] = useState(false);

  const onNotify = useCallback(() => {
    onAuthorize?.();
  }, [onAuthorize]);

  const handleDownload = useCallback(async () => {
    if (!onDownload) return;

    setIsDownloading(true);
    try {
      await onDownload(mandate);
    } finally {
      setIsDownloading(false);
    }
  }, [mandate, onDownload]);

  const onPreview = useCallback(() => {
    if (!mandate.mandate_image_file) return;
    window.open(getCORSSafeURL(mandate.mandate_image_file), "_blank");
  }, [mandate.mandate_image_file]);

  const showStatusCard = shouldShowStatusCard(mandate);
  const showUploadButton = showUploadSignedForm(mandate) && !!onSelect;
  const mandateTypeLabel =
    mandate.mandate_type === MandateType.E ? "eNACH" : "Physical";

  return (
    <>
      <div className={cn("flex flex-col gap-3", props.className)}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <span className="text-foreground">{mandateTypeLabel}</span>
            <span>•</span>
            <span>
              {mandate.end_date ? formatMandateEndDate(mandate.end_date) : ""}
            </span>
          </div>
          <div className="flex flex-col items-end gap-2">
            {showActiveHeaderBadge(mandate) && (
              <span
                className={cn(
                  "text-sm font-semibold",
                  getStatusColor(mandate.status)
                )}
              >
                {mandate.status}
              </span>
            )}
            {mandate.status === MandateStatus.REJECTED && onDelete && (
              <Button
                onClick={() => onDelete(mandate)}
                variant="ghost"
                size="icon"
                className="size-8"
              >
                <Trash2Icon className="size-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-foreground">
            {mandate.bank_name} *{mandate.bank_account_number.slice(-4)}
          </span>
          <span className="text-sm font-semibold">
            {convertToCurrency(mandate.mandate_limit.toString())}
          </span>
        </div>

        {showStatusCard ? (
          <MandateStatusCard
            mandate={mandate}
            onNotify={onNotify}
            onSelfAuthorize={onSelfAuthorize}
            onDownload={handleDownload}
            onPreview={onPreview}
            isNotifying={isNotifying}
            isDownloading={isDownloading}
            showReviewAction={!!onAuthorize}
            showNotifyAction={!!onAuthorize}
            showDownloadAction={!!onDownload}
          />
        ) : null}

        {showUploadButton ? (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => onSelect?.(mandate)}
          >
            Upload signed form
          </Button>
        ) : null}
      </div>
    </>
  );
}
